<template>
  <div class="modal fade" id="modal" ref="modal" data-bs-backdrop="static" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content border-0 shadow-lg" style="border-radius: 16px;">

        <div class="bg-danger modal-header pe-4 text-white" style="border-radius: 16px 16px 0 0;">
          <div>
            <h4 class="modal-title fw-bold mb-0">
              <i :class="headerIcon" class="me-2"/>
              {{ headerTitle }}
            </h4>
            <div class="small opacity-75 mt-1 d-flex flex-wrap gap-3">
              <span>
                <i class="fa-regular fa-clock me-1"/>
                {{ seatedLabel }}
              </span>
              <span v-if="isTable && staff">
                <i class="fa-solid fa-user-tie me-1"/>{{ staff }}
              </span>
              <span v-if="!isTable">
                <i :class="typeIcon" class="me-1"/>{{ $t(typeLabel) }}
              </span>
              <span v-if="!isTable && isDineIn">
                <i class="fa-solid fa-chair me-1"/>{{ session.table_name || $t('No table') }}
              </span>
            </div>
          </div>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"/>
        </div>

        <div class="modal-body px-4 py-4">

          <div class="card border-0 shadow-sm rounded-3 mb-2">
            <div class="card-body">

              <!-- Loading State -->
              <div v-if="loadingSummary" class="text-center py-4">
                <div class="spinner-border text-danger" role="status">
                  <span class="visually-hidden">{{ $t('Loading') }}...</span>
                </div>
                <div class="text-muted small mt-2">{{ $t('Loading') }}...</div>
              </div>

              <!-- Empty State -->
              <div v-else-if="!hasItems" class="text-center py-4">
                <i class="fa-solid fa-utensils text-muted fs-1 mb-3"/>
                <div class="text-muted fw-bold">{{ $t('No items') }}</div>
                <div class="text-muted small">{{ $t('This table has no orders yet') }}</div>
              </div>

              <!-- Data State -->
              <template v-else>
                <div class="d-flex justify-content-between">
                  <div class="text-muted small fw-bold">
                    {{ $t('Total') + ' (' + itemsCount + ' ' + $t('items') + ')' }}
                  </div>
                  <div class="fs-3 fw-bold text-dark">{{ $storeIndex.formatThaiCurrency(totalAmount) }}</div>
                </div>

                <div v-if="hasBreakdown" class="d-flex flex-wrap gap-2 border-top pt-3 mt-3">

                  <div v-if="summary.count_queued" class="flex-grow-1" style="min-width: 100px;">
                    <div class="badge bg-secondary d-flex justify-content-between">
                      <span><i class="fa-solid fa-hourglass-half me-1"/>{{ $t('_POS.Pending') }}</span>
                      <span>{{ summary.count_queued }}</span>
                    </div>
                  </div>

                  <div v-if="summary.count_progressing" class="flex-grow-1" style="min-width: 100px;">
                    <div class="badge bg-warning text-dark d-flex justify-content-between">
                      <span><i class="fa-solid fa-fire-burner me-1"/>{{ $t('_POS.Cooking') }}</span>
                      <span>{{ summary.count_progressing }}</span>
                    </div>
                  </div>

                  <div v-if="summary.count_ready" class="flex-grow-1" style="min-width: 100px;">
                    <div class="badge bg-info text-dark d-flex justify-content-between">
                      <span><i class="fa-solid fa-bell-concierge me-1"/>{{ $t('_POS.To Serve') }}</span>
                      <span>{{ summary.count_ready }}</span>
                    </div>
                  </div>

                  <div v-if="summary.count_completed" class="flex-grow-1" style="min-width: 100px;">
                    <div class="badge bg-success d-flex justify-content-between">
                      <span><i class="fa-solid fa-check-double me-1"/>{{ $t('_POS.Served') }}</span>
                      <span>{{ summary.count_completed }}</span>
                    </div>
                  </div>

                  <div v-if="summary.count_cancelled" class="flex-grow-1" style="min-width: 100px;">
                    <div class="badge bg-danger d-flex justify-content-between">
                      <span><i class="fa-solid fa-ban me-1"/>{{ $t('_POS.Cancelled') }}</span>
                      <span>{{ summary.count_cancelled }}</span>
                    </div>
                  </div>

                </div>
              </template>

              <!-- Customer / member — session variant only (mirrors old ModalSessionActions) -->
              <div v-if="showMemberSelect" class="small mt-3 border-top pt-3">
                <div>
                  <i class="fa-solid fa-user me-2 text-muted"/>
                  <strong>{{ session.customer_name || $t('Walk-in') }}</strong>
                </div>
                <div v-if="session.telephone" class="text-muted">
                  <i class="fa-solid fa-phone me-2"/>{{ session.telephone }}
                </div>
                <div v-if="formattedPickup" class="text-muted">
                  <i class="fa-regular fa-clock me-2"/>{{ $t('Pickup') }} {{ formattedPickup }}
                </div>
                <div class="mt-2">
                  <member-select
                    :dropdown-parent="'#modal'"
                    :placeholder="$t('Attach Member') + '...'"
                    @selected="onSetMember"
                    @cleared="onClearMember"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="d-grid gap-2">

            <!-- PRIMARY — the one thing staff nearly always came here to do -->
            <button
              type="button"
              class="btn btn-theme py-3 fw-bold fs-5 text-white"
              style="border-radius: 12px;"
              @click="goToOrder"
            >
              <i class="fa-solid fa-bell-concierge me-2"/>
              {{ $t('Order Food / Manage Order') }}
            </button>

            <!-- SECONDARY-PRIMARY — Check Bill, the natural next step; only once there's something to pay for -->
            <button
              v-if="hasItems"
              type="button"
              class="btn btn-success py-3 fw-bold rounded-3 text-white"
              data-bs-dismiss="modal"
              @click="$emit('check-bill')"
            >
              <i class="fa-solid fa-file-invoice-dollar me-2"/>
              {{ $t('Check Bill') }}
              <span class="opacity-75 ms-1">· {{ $storeIndex.formatThaiCurrency(totalAmount) }}</span>
            </button>

            <!--
              Compact icon toolbar — everything secondary collapses into one
              row of small tiles instead of a stack of full-width bars. The
              "call" tile escalates (red + pulsing dot) when a request is
              pending, so an alert still stands out without its own banner.
            -->
            <!--
              QR บริการ + รายการเรียกพนักงาน ซ่อนเมื่อแพ็กไม่มี feature_qr_ordering
              (คำขอเรียกพนักงาน/เรียกเก็บเงิน มาจากหน้า QR ลูกค้าทางเดียว — services/requests, services/bills/request)
            -->
            <div class="d-flex gap-2">
              <button v-if="qrOrdering" type="button" class="action-tile" @click="showQrCode = !showQrCode">
                <i class="fa-solid fa-qrcode"/>
                <span>{{ $t('QR Service') }}</span>
              </button>
              <button
                v-if="canMove"
                type="button"
                class="action-tile"
                data-bs-dismiss="modal"
                @click="$emit('move-table')"
              >
                <i class="fa-solid fa-arrow-right-arrow-left"/>
                <span>{{ $t('Move Table') }}</span>
              </button>
              <button
                type="button"
                class="action-tile"
                data-bs-dismiss="modal"
                @click="$emit('merge-session')"
              >
                <i class="fa-solid fa-object-group"/>
                <span>{{ $t('Merge Session') }}</span>
              </button>
              <button
                v-if="qrOrdering"
                type="button"
                class="action-tile position-relative"
                :class="{ 'action-tile--alert': session.is_request }"
                data-bs-dismiss="modal"
                @click="$emit('manage-requests')"
              >
                <i class="fa-solid fa-bell"/>
                <span>{{ $t('Service Requests') }}</span>
                <span
                  v-if="session.is_request"
                  class="position-absolute top-0 end-0 translate-middle p-1 bg-danger rounded-circle alert-dot"
                >
                  <span class="visually-hidden">{{ $t('Pending request') }}</span>
                </span>
              </button>
            </div>

            <!-- QR panel -->
            <div v-if="qrOrdering" v-show="showQrCode">
              <div class="card border-0 shadow-sm rounded-3">
                <div class="card-body text-center py-4">
                  <div class="fw-bold mb-2">{{ $t('Scan to Service') }}</div>
                  <canvas ref="qrCanvas"></canvas>
                  <div class="mt-3 d-flex justify-content-center gap-2 flex-wrap">
                    <button type="button" class="btn btn-sm btn-dark px-4 rounded-3" @click="printQrCode">
                      <i class="fa-solid fa-print me-2"/>{{ $t('Print QR Code') }}
                    </button>
                    <button type="button" class="btn btn-sm btn-outline-dark px-4 rounded-3" @click="copyServiceUrl">
                      <i :class="copied ? 'fa-solid fa-check' : 'fa-solid fa-copy'" class="me-2"/>
                      {{ copied ? $t('Copied') : $t('Copy URL') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- DESTRUCTIVE — de-emphasised text link at the very bottom -->
            <button
              type="button"
              class="btn btn-link text-danger text-decoration-none fw-semibold py-2 mt-1 end-service-link"
              :disabled="loadingClose"
              @click="handleEndService"
            >
              <i class="fa-solid fa-spinner fa-spin me-1" v-if="loadingClose"/>
              <i class="fa-solid fa-flag-checkered me-1" v-else/>
              {{ $t('End Service') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { POSTables, Sessions } from '@/services/endpoint/pos.service';
import MemberSelect from '@/components/shared/MemberSelect.vue';
import QRCode from 'qrcode';
import { printQrSlips } from '@/utils/qrPrint';
import moment from 'moment';

/**
 * Unified table / session action sheet.
 *
 * Replaces the near-identical ModalTableOccupied (tables view) and
 * ModalSessionActions (sessions view). Callers pass one normalized `session`
 * object plus a `variant`:
 *   - variant 'table'   → header shows the table name + seated-for + staff,
 *                         summary is fetched by table id, no member select.
 *   - variant 'session' → header shows "Session #orderNo" + type + table,
 *                         summary is fetched by token, member select shown.
 *
 * Buttons are grouped and adapt to state: an empty session (no orders) opens
 * on just the primary "Order" action with the rest collapsed behind "More".
 */
export default {
  name: 'SessionActionsModal',
  components: { MemberSelect },
  props: {
    // Normalized session-like object. Required fields: token. Optional:
    // name, table_name, type, service_staff, service_at / created_at,
    // customer_name, telephone, is_request, pickup_at, items_count, total.
    session: { type: Object, required: true },
    variant: { type: String, default: 'session' }, // 'table' | 'session'
    orderNo: { type: String, default: '' },
    // When > 0 the summary is fetched via the table endpoint (table variant).
    tableId: { type: Number, default: 0 },
  },
  emits: ['close', 'check-bill', 'move-table', 'merge-session', 'manage-requests', 'closed'],
  data() {
    return {
      showQrCode: false,
      loadingClose: false,
      copied: false,
      loadingSummary: true,
      summary: null,
    };
  },
  computed: {
    // แพ็กมีสั่งผ่าน QR ไหม — ไม่มี = ซ่อนปุ่ม QR (ลูกค้าสแกนไปก็เจอจอ "ระบบสั่งอาหารไม่พร้อมใช้งาน")
    qrOrdering() {
      return this.$hasFeature('feature_qr_ordering');
    },
    isTable() {
      return this.variant === 'table';
    },
    staff() {
      return this.session.service_staff || this.session.staff || '';
    },
    seatedAt() {
      return this.session.service_at || this.session.created_at || '';
    },
    seatedLabel() {
      const diff = this.seatedAt ? this.$storeIndex.getTimeDiff(this.seatedAt) : '-';
      return this.isTable ? this.$t('Seated for') + ' ' + diff : diff;
    },
    headerIcon() {
      return this.isTable ? 'fa-solid fa-users' : this.typeIcon;
    },
    headerTitle() {
      return this.isTable
        ? this.$t('Table') + ' ' + (this.session.name || this.session.table_name || '')
        : this.$t('Session') + ' #' + this.orderNo;
    },
    itemsCount() {
      return Number(this.summary?.count_items ?? this.session.items_count ?? 0);
    },
    totalAmount() {
      return this.summary?.sum_total ?? this.session.total ?? 0;
    },
    hasItems() {
      return this.itemsCount > 0;
    },
    hasBreakdown() {
      const s = this.summary;
      if (!s) return false;
      return !!(s.count_queued || s.count_progressing || s.count_ready
        || s.count_completed || s.count_cancelled);
    },
    showMemberSelect() {
      return !this.isTable;
    },
    isDineIn() {
      return this.isTable || this.session.type === 'dine_in';
    },
    canMove() {
      return this.isDineIn;
    },
    typeIcon() {
      const map = {
        dine_in: 'fa-solid fa-utensils',
        takeaway: 'fa-solid fa-bag-shopping',
        delivery: 'fa-solid fa-motorcycle',
      };
      return map[this.session.type] || 'fa-solid fa-receipt';
    },
    typeLabel() {
      const map = { dine_in: 'Dine-in', takeaway: 'Takeaway', delivery: 'Delivery' };
      return map[this.session.type] || this.session.type;
    },
    serviceUrl() {
      return process.env.VUE_APP_URL + '/service/' + this.session.token;
    },
    formattedPickup() {
      if (!this.session.pickup_at) return '';
      const m = moment(this.session.pickup_at);
      return m.isValid() ? m.format('HH:mm') : this.session.pickup_at;
    },
  },
  methods: {
    async fetchSummary() {
      this.loadingSummary = true;
      try {
        const res = this.tableId
          ? await POSTables.getSummary(this.tableId)
          : await Sessions.summary(this.session.token);
        this.summary = res.data.data;
      } catch (err) {
        this.summary = null;
      } finally {
        this.loadingSummary = false;
      }
    },
    async onSetMember(member) {
      try {
        await Sessions.setCustomer(this.session.token, {
          customer_id: member.id,
          customer_name: member.fullname,
          telephone: member.telephone,
        });
        this.session.customer_id = member.id;
        this.session.customer_name = member.fullname;
        this.session.telephone = member.telephone;
        this.$toast.success('<B>' + this.$t('Success') + '</B>\n' + this.$t('Member attached.'));
      } catch (err) {
        this.$toast.warn(err?.response?.data?.message || this.$t('Failed to attach member'));
      }
    },
    async onClearMember() {
      try {
        await Sessions.setCustomer(this.session.token, { customer_id: null });
        this.session.customer_id = null;
      } catch (err) {
        this.$toast.warn(err?.response?.data?.message);
      }
    },
    goToOrder() {
      $(this.$refs.modal).modal('hide');
      this.$router.push({
        name: 'pos.sessions.detail',
        params: { token: this.session.token },
        query: { from: this.isTable ? 'table' : 'sessions' },
      });
    },
    handleEndService() {
      const title = this.isTable
        ? this.$t('End service at') + ' ' + (this.session.name || this.session.table_name || '') + '?'
        : this.$t('End service for') + ' #' + this.orderNo + '?';
      const detail = this.isTable
        ? this.$t('This will close the service session and release the table for new guests.')
        : this.$t('This will close the service session.');
      const warning = this.$t('Any unpaid bills will remain on file — settle them first if needed.');
      this.$swal({
        icon: 'warning',
        iconColor: '#FF7043',
        title,
        html: `<div class="text-start mb-2">${detail}</div>`
          + `<div class="small text-muted text-start"><i class="fa-solid fa-circle-info me-1"></i>${warning}</div>`,
        showCancelButton: true,
        cancelButtonText: this.$t('Cancel'),
        confirmButtonText: this.$t('End Service'),
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-secondary me-2',
        },
        reverseButtons: true,
      }).then(async res => {
        if (!res.isConfirmed) return;
        this.loadingClose = true;
        await Sessions.close(this.session.token).then(response => {
          const msg = response?.data?.data?.message
            || response?.data?.message
            || this.$t('Session closed');
          this.$toast.success('<b>' + this.$t('Success') + '</b> ' + msg);
          $(this.$refs.modal).modal('hide');
          this.$emit('closed');
        }).catch(err => {
          if (err?.response?.data?.message) {
            this.$toast.warn(err.response.data.message);
          }
        }).finally(() => {
          this.loadingClose = false;
        });
      });
    },
    async copyServiceUrl() {
      const url = this.serviceUrl;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(url);
        } else {
          const ta = document.createElement('textarea');
          ta.value = url;
          ta.style.cssText = 'position:fixed;top:-9999px;left:-9999px;';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          ta.remove();
        }
        this.copied = true;
        this.$toast.success(this.$t('Copied'));
        setTimeout(() => { this.copied = false; }, 2000);
      } catch (e) {
        this.$toast.error(this.$t('Copy failed') + ': ' + (e.message || e));
      }
    },
    async renderQrCode() {
      await this.$nextTick();
      if (this.$refs.qrCanvas) {
        QRCode.toCanvas(this.$refs.qrCanvas, this.serviceUrl, { width: 200, margin: 1 });
      }
    },
    async printQrCode() {
      const heading = this.session.table_name
        ? this.$t('Table') + ' ' + this.session.table_name
        : (this.orderNo ? this.$t('Session') + ' #' + this.orderNo
          : this.$t('Table') + ' ' + (this.session.name || ''));
      try {
        await printQrSlips(
          [{ heading, subtitle: this.$t('Scan to Service'), url: this.serviceUrl }]
        );
      } catch (e) {
        console.error('QR print error:', e);
        this.$toast.error(this.$t('Print failed') + ': ' + (e.message || e));
      }
    },
  },
  watch: {
    showQrCode(val) {
      if (val) this.renderQrCode();
    },
  },
  mounted() {
    $(this.$refs.modal).modal('show');
    $(this.$refs.modal).on('shown.bs.modal', () => this.fetchSummary());
    $(this.$refs.modal).on('hidden.bs.modal', () => this.$emit('close'));
  },
};
</script>

<style scoped>
/* Compact secondary-action tiles: icon over a tiny label, sharing one row. */
.action-tile {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 12px 4px;
  border: 1px solid var(--bs-border-color, #dee2e6);
  border-radius: 12px;
  background: #fff;
  color: #495057;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.1;
  transition: background .12s ease, border-color .12s ease, color .12s ease;
}
.action-tile i {
  font-size: 1.2rem;
}
.action-tile span {
  max-width: 100%;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.action-tile:hover {
  background: #f8f9fa;
  border-color: #ced4da;
}
/* Escalated "call" tile when a customer request is pending. */
.action-tile--alert {
  color: #dc3545;
  border-color: #f1aeb5;
  background: #fdf1f2;
}
.action-tile--alert:hover {
  background: #fbe3e5;
  border-color: #ea868f;
}

.end-service-link:hover {
  color: #a71d2a !important;
}

.alert-dot {
  width: 10px;
  height: 10px;
  animation: alert-dot-pulse 1.2s ease-in-out infinite;
}
@keyframes alert-dot-pulse {
  0%, 100% { box-shadow: 0 0 0 0   rgba(220, 53, 69, 0.55); }
  50%      { box-shadow: 0 0 0 6px rgba(220, 53, 69, 0);    }
}
</style>
